import {
    cleanNumericInput,
    currencyOptions,
    MAX_MONTHLY_INCOME,
    MAX_ANNUAL_INCOME,
} from '../../../utils/income-utils';
import {
    regexValidation,
    wrapTrademarkSuperscript,
} from '../../../utils/cob-utils';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';
import { FunctionPipe } from '../../../utils/FunctionPipe';
import {
    IncomeCalculator,
    IncomeCalculatorButton,
    useIncomeCalculatorHandling,
} from './IncomeCalculator';
import './MonthlyIncomeField.css';
import IncomePopup from './IncomePopup';
import { InputBase, InputRadio } from 'component-library/index';
import useMonthlyIncomeHandling from './useMonthlyIncomeHandling';
import { useAcquisitionContext } from '../../../providers/AcquisitionProvider/AcquisitionProvider';
import AcquisitionErrorToggleEvent from '../../ApplicationCta/AcquisitionErrorToggleEvent';
import SessionStorage from '../../../utils/SessionStorage';
import SlideDown from '../../SlideDown/SlideDown';
import { generateFieldErrorMessage } from '../../../utils/error-message';
import { isNewErrorMessagingFlagActive } from '../../../utils/error-message-flag';

export interface IMonthlyIncomeFieldProps
    extends React.HTMLAttributes<HTMLInputElement> {
    monthlyIncomeLabel?: string;
    monthlyIncome: string;
    incomeMessage?: string;
    incomeDescription?: string;
    inputClass?: string;
    popUp?: boolean;
    popUpHeadline?: string;
    popUpSubHeadline?: string;
    popUpCtaKeep?: string;
    popUpCtaDismiss?: string;
    handleSetValue: (newValue: string) => void;
    handleIsValidCallback?: (isValid: boolean, value?: string) => void;
    includeSuccessIcon?: boolean;
    onBlurCallback?: (value: string, isValid?: boolean) => void;
    isIncomeCalculator?: boolean;
    isGrossIncomeVariant?: boolean;
    alternate?: boolean;
}

const MonthlyIncomeField = forwardRef(
    (
        {
            monthlyIncomeLabel,
            monthlyIncome,
            incomeMessage,
            incomeDescription,
            popUp,
            popUpHeadline,
            popUpSubHeadline,
            popUpCtaKeep,
            popUpCtaDismiss,
            handleSetValue: setMonthlyIncome,
            handleIsValidCallback = () => {},
            onBlurCallback,
            isGrossIncomeVariant = false,
            isIncomeCalculator = !isGrossIncomeVariant,
            ...props
        }: IMonthlyIncomeFieldProps,
        inputRef: any,
    ) => {
        const [showPopUp, setShowPopUp] = useState(false);
        const [popupHasBeenShown, setPopupHasBeenShown] = useState(false);
        const [showModal, setShowModal] = useState(false);

        const [showDesc, setShowDesc] = useState(false);
        const [showLongDesc, setShowLongDesc] = useState(false);
        const [showSuccessIcon, setShowSuccessIcon] = useState(false);
        const [isBlurred, setIsBlurred] = useState(false);
        const [inputError, setInputError] = useState(false);
        const [inputErrorText, setInputErrorText] = useState<string>('');

        const [ calculatorValue, setCalculatorValue ] = useState('');

        const {
            setIsMonthlyIncomeValid,
            isMonthlyOrYearly,
            setIsMonthlyOrYearly,
        } = useAcquisitionContext();
        const [radioValue, setRadioValue] =
            useState<'' | 'monthly' | 'yearly'>(isMonthlyOrYearly);

        const { errorMessages, incomeLimits, convertValue, convertToAnnual } =
            useMonthlyIncomeHandling();


        // TODO: If Yearly Income Variant wins the A/B Test, refactor this to be part of PersonalInfoForm
        useEffect(() => {
            if (isGrossIncomeVariant) {
                if (
                    radioValue === '' &&
                    SessionStorage.verifyNotEmpty('inc')
                ) {
                    setIsMonthlyOrYearly(SessionStorage.getObject('inc')[0] || '');
                    setRadioValue(SessionStorage.getObject('inc')[0] || '');
                } else if (radioValue !== '') {
                    SessionStorage.setObject('inc', [
                        radioValue,
                        popupHasBeenShown,
                    ]);
                    setIsMonthlyOrYearly(radioValue);
                }
            }
        }, [radioValue, popupHasBeenShown, setIsMonthlyOrYearly]);

        const handleMonthlyIncomeFocus = useCallback(() => {
            if (isGrossIncomeVariant && !radioValue) {
                setRadioValue('monthly');
            }
        }, [isGrossIncomeVariant, radioValue, setRadioValue]);

        const validateGrossIncome = useCallback(
            (valueToValidate: string, validatingRadioValue = 'monthly') => 
                FunctionPipe.pipe(
                    valueToValidate || '',
                    { stopOnError: true },
                )
                    .transform((rawValue: string) => {
                        const cleanValue = cleanNumericInput(rawValue);
                        const numericValue = parseFloat(cleanValue);
                        if (!cleanValue || isNaN(numericValue)) {
                            throw errorMessages.MISSING_VALUE;
                        }
                        return {
                            raw: rawValue,
                            clean: cleanValue,
                            numeric: numericValue,
                        };
                    })
                    .validate((values) => ({
                        valid: !!values.clean && values.clean.length > 0,
                        error: errorMessages.DEFAULT,
                    }))
                    .validate((values) => ({
                        valid: !isNaN(values.numeric) && values.numeric > 0,
                        error: isNewErrorMessagingFlagActive ? errorMessages.EXCEEDS_MIN : errorMessages.DEFAULT,
                    }))
                    .validate((values) => ({
                        valid:
                            values.clean.length >=
                            incomeLimits.lengthRequirement[
                                validatingRadioValue
                            ],
                        error: errorMessages.VAL_TOO_SMALL,
                    }))
                    .validate((values) => ({
                        valid:
                            values.numeric >=
                            incomeLimits.min[validatingRadioValue],
                        error: errorMessages.EXCEEDS_MIN,
                    }))
                    .validate((values) => ({
                        valid:
                            values.numeric <=
                            incomeLimits.max[validatingRadioValue],
                        error: errorMessages.EXCEEDS_MAX,
                    }))
                    .transform((values) => 
                        validatingRadioValue === 'yearly'
                        ? ({...values,
                            monthly: convertValue(values.numeric, 'yearly'),
                            annual: values.numeric.toLocaleString(
                                'en-US',
                                currencyOptions,
                            ),
                        })
                        : ({...values,
                            monthly: values.numeric.toLocaleString(
                                'en-US',
                                currencyOptions,
                            ),
                            annual: convertValue(values.numeric, 'monthly'),
                        })
                    )
            ,[
                radioValue,
                monthlyIncome,
                incomeLimits,
                errorMessages,
                cleanNumericInput,
            ],
        );

        const validateMonthlyIncome = useCallback(
            (event: {target?: {value?: string }}) =>
                FunctionPipe.pipe(
                    event?.target?.value ?? monthlyIncome,
                    { stopOnError: true },
                )
                    .transform(rawValue => {
                        const cleanValue = cleanNumericInput(rawValue);
                        const numericValue = parseFloat(cleanValue);
                        if (!cleanValue || isNaN(numericValue)) {
                            throw errorMessages.MISSING_VALUE;
                        }
                        return {
                            raw: rawValue,
                            clean: cleanValue,
                            numeric: numericValue,
                        };
                    })
                    .validate((values) => ({
                        valid: !!values.clean && values.clean.length > 0,
                        error: errorMessages.DEFAULT,
                    }))
                    .validate((values) => ({
                        valid: !isNaN(values.numeric) && values.numeric > 0,
                        error: isNewErrorMessagingFlagActive ? errorMessages.EXCEEDS_MIN : errorMessages.DEFAULT,
                    }))
                    .validate((values) => ({
                        valid:
                            values.clean.length >=
                            incomeLimits.lengthRequirement.monthly,
                        error: errorMessages.VAL_TOO_SMALL,
                    }))
                    .validate((values) => ({
                        valid: values.numeric >= incomeLimits.min.monthly,
                        error: errorMessages.EXCEEDS_MIN,
                    }))
                    .validate((values) => ({
                        valid: values.numeric <= incomeLimits.max.monthly,
                        error: errorMessages.EXCEEDS_MAX,
                    }))
                    .transform((values) => ({
                        ...values,
                        monthly: values.numeric.toLocaleString(
                            'en-US',
                            currencyOptions,
                        ),
                        annual: convertValue(values.numeric, 'monthly'),
                    })),
            [monthlyIncome, errorMessages, incomeLimits, cleanNumericInput],
        );

        /**
         * Once the Gross Income Test is complete, consider refactoring to remove isAcquisitionErrorToggleEvent parameter.
         */
        const handleMonthlyIncomeBlur = useCallback(
            (event?: { target?: { value?: string }}, isValueMonthly = radioValue !== 'yearly', isAcquisitionErrorToggleEvent: boolean = false) => {
                const result = isGrossIncomeVariant
                    ? validateGrossIncome(
                        event?.target?.value || monthlyIncome,
                        isValueMonthly
                            ? 'monthly'
                            : radioValue,
                    )
                    : validateMonthlyIncome(event);
                const valid = !result.hasErrors();
                const finalValue = result.getValue() || {
                    monthly: '',
                    annual: '',
                    numeric: 0,
                };

                const incomeValue: string = finalValue.monthly ?? event?.target?.value ?? monthlyIncome;
                onBlurCallback && onBlurCallback(incomeValue, valid);
                if (valid) {
                    if (!isAcquisitionErrorToggleEvent && !popupHasBeenShown) {
                        const numericIncome = finalValue.numeric;
                        if (
                            numericIncome <
                            incomeLimits.popupThreshold[radioValue || 'monthly']
                        ) {
                            setShowPopUp(true);
                            setPopupHasBeenShown(true);
                        }
                    }
                    setMonthlyIncome(finalValue.monthly);
                    handleIsValidCallback(true, finalValue.monthly);
                    setIsMonthlyIncomeValid(true);

                    setShowSuccessIcon(true);
                    setInputErrorText('');
                    setInputError(false);
                } else {
                    handleIsValidCallback(false, incomeValue);
                    setIsMonthlyIncomeValid(false);

                    setShowSuccessIcon(false);
                    setInputError(true);
                    if (!inputErrorText || !isValueMonthly) {
                        setInputErrorText(
                            result?.getErrors()[0]?.error[radioValue || 'monthly'],
                        );
                    }
                }
            },
            [
                monthlyIncome,
                isGrossIncomeVariant,
                radioValue,
                validateGrossIncome,
                validateMonthlyIncome,
                setIsMonthlyIncomeValid,
                onBlurCallback,
                setMonthlyIncome,
                handleIsValidCallback,
                setInputError,
                setInputErrorText,
                popupHasBeenShown,
                setPopupHasBeenShown,
                setShowPopUp,
                setShowSuccessIcon,
            ],
        );

        useEffect(() => {
            const acquisitionErrorListener = (
                event: AcquisitionErrorToggleEvent,
            ) => {
                if (isGrossIncomeVariant && !radioValue) {
                    setRadioValue('monthly');
                }
                handleMonthlyIncomeBlur(
                    { target: { value: event?.getValue('monthlyIncome') } },
                    true,
                    true,
                );
            };

            window.addEventListener(
                AcquisitionErrorToggleEvent.EventName,
                acquisitionErrorListener,
            );
            return () => {
                window.removeEventListener(
                    AcquisitionErrorToggleEvent.EventName,
                    acquisitionErrorListener,
                );
            };
        }, [
            radioValue
        ]);

        const handleSetValueInternally = useCallback(
            (val: string): void => {
                FunctionPipe.pipe(val, { stopOnError: true })
                    .transform(cleanNumericInput)
                    .transform(cleaned => {
                        if (!isNewErrorMessagingFlagActive && cleaned && /^0+$/.test(cleaned)) {
                            throw 'Invalid Value';
                        }
                        setInputError(false);
                        setInputErrorText('');
                        return { numeric: parseInt(cleaned) };
                    })
                    .transform(({ numeric }) => {
                        const MAX_INCOME =
                            radioValue === 'yearly'
                                ? MAX_ANNUAL_INCOME
                                : MAX_MONTHLY_INCOME;
                        if (isNaN(numeric)) {
                            setMonthlyIncome('');
                            throw 'Invalid value';
                        } else if ((!isNewErrorMessagingFlagActive && numeric === 0) || numeric < 0 || numeric > MAX_INCOME) {
                            throw 'Exceeds allowed range';
                        }
                        return { numeric };
                    })
                    .transform(({ numeric }) => {
                        if (radioValue === 'yearly') {
                            setMonthlyIncome(convertValue(numeric, 'yearly'));
                            setCalculatorValue(numeric.toLocaleString('en-US', currencyOptions));
                        } else {
                            setMonthlyIncome(
                                numeric.toLocaleString(
                                    'en-US',
                                    currencyOptions,
                                ),
                            );
                        }
                    });
            },
            [
                radioValue,
                currencyOptions,
                MAX_ANNUAL_INCOME,
                MAX_MONTHLY_INCOME,
                cleanNumericInput,
                setInputError,
                setInputErrorText,
                setMonthlyIncome,
            ],
        );

        const updateRadioValue = useCallback((nextValue: 'yearly' | 'monthly') => {
            if (nextValue === 'yearly') {
                setCalculatorValue(convertToAnnual(monthlyIncome));
            } else if (nextValue === 'monthly') {
                setMonthlyIncome(convertValue(calculatorValue, 'yearly'));
            }
            setRadioValue(nextValue);
        }, [
            setRadioValue,
            setCalculatorValue,
            convertToAnnual,
        ]);

        useEffect(() => {
            if ( isGrossIncomeVariant && inputError ) {
                handleMonthlyIncomeBlur({target: {value: monthlyIncome } }, false);
            }
        }, [radioValue]);

        useEffect(() => {
            const numericValue = +cleanNumericInput(monthlyIncome);
            setShowSuccessIcon(
                inputRef?.current !== document.activeElement
                && !inputError
                && numericValue >= 100
                && numericValue <= MAX_MONTHLY_INCOME
                && regexValidation().allow.monthlyIncome.test(monthlyIncome)
            );
        }, [
            radioValue,
            monthlyIncome,
            setShowSuccessIcon,
            convertValue,
            cleanNumericInput,
            regexValidation,
        ]);

        const { handleOpenCalculator, closeCalculatorCallback } =
            useIncomeCalculatorHandling({
                monthlyIncomeRef: inputRef,
                monthlyIncome,
                calculatorValue,
                setCalculatorValue,
                setShowModal,
                setShowDesc,
                handleMonthlyIncomeBlur,
                convertValue,
            });

        return (
            <div
                className={`MonthlyIncome flex flex-col ${
                    isGrossIncomeVariant
                        ? `MonthlyIncome--${radioValue || ''}GrossIncome`
                        : ''
                }`}
                onFocus={() => setShowDesc(true)}
                onBlur={e => {
                    const currentTarget = e.currentTarget;
                    const relatedTarget = e.relatedTarget;

                    if (!currentTarget.contains(relatedTarget as Node)) {
                        setShowDesc(false);
                    }
                }}
            >
                {popUp && showPopUp && (
                    <IncomePopup
                        monthlyIncomeRef={inputRef}
                        monthlyIncome={monthlyIncome}
                        title={popUpHeadline}
                        subtitle={popUpSubHeadline}
                        radioValue={radioValue}
                        successText={popUpCtaKeep}
                        cancelText={popUpCtaDismiss}
                        setShowPopUp={setShowPopUp}
                        setMonthlyIncome={setMonthlyIncome}
                        convertToAnnual={convertToAnnual}
                    />
                )}
                <div className='MonthlyIncome__fieldAndButton flex flex-col'>
                    {isGrossIncomeVariant && (
                        <>
                            <label
                                htmlFor='monthlyIncome'
                                className='Label'
                            >
                                <div className='Label__text_wrapper'>
                                    <span className='Label__text'>
                                        Gross Income
                                        <sup className='Label__text__required'>
                                            *
                                        </sup>
                                    </span>
                                </div>
                            </label>
                            <div className='InputBase__radios'>
                                <InputRadio
                                    label='Monthly'
                                    value='monthly'
                                    name='monthly_yearly_group'
                                    id='monthly--radio'
                                    setValue={() => updateRadioValue('monthly')}
                                    checked={radioValue === 'monthly'}
                                />
                                <InputRadio
                                    label='Yearly'
                                    value='yearly'
                                    name='monthly_yearly_group'
                                    id='yearly--radio'
                                    setValue={() => {
                                        updateRadioValue('yearly')
                                    }}
                                    checked={radioValue === 'yearly'}
                                />
                            </div>
                        </>
                    )}
                    <InputBase
                        required
                        hideAlertIcon
                        useSuccessIcon
                        ref={inputRef}
                        name='MonthlyIncome'
                        id='monthlyIncome'
                        type='tel'
                        className={`MonthlyIncome__incomeInput${
                            inputError ? ' error' : ''
                        }${!isIncomeCalculator ? ' w-full' : ''}${
                            isGrossIncomeVariant ? ' self-start' : ''
                        } ${radioValue}`}
                        tooltipNotchPosition='top-left'
                        mobileTooltipNotchPosition='top-center'
                        requiredMessage={
                            isGrossIncomeVariant
                                ? errorMessages.MISSING_VALUE[radioValue]
                                : generateFieldErrorMessage('Total Monthly Income', 'missingValue')
                        }
                        inputClass='pl-12'
                        inputChild={
                            <span
                                className='MonthlyIncome__inputPrefix'
                                aria-hidden={true}
                                tabIndex={-1}
                            >
                                $
                            </span>
                        }
                        value={
                            radioValue === 'yearly'
                                ? calculatorValue
                                : monthlyIncome
                        }
                        setValue={handleSetValueInternally}
                        onFocus={() => {
                            setIsBlurred(false);
                            handleMonthlyIncomeFocus();
                        }}
                        onBlur={() => {
                            setIsBlurred(true);
                            handleMonthlyIncomeBlur();
                        }}
                        onPaste={(e: any) => e.preventDefault()}
                        autoComplete='off'
                        maxLength={isGrossIncomeVariant ? 11 : 7}
                        label={
                            !isGrossIncomeVariant&&
                            transformHTMLParserLinks(
                                wrapTrademarkSuperscript(monthlyIncomeLabel),
                            )
                        }
                        aria-label={
                            isGrossIncomeVariant
                                ? 'Gross Income'
                                : monthlyIncomeLabel
                        }
                        showSuccessIcon={isBlurred && showSuccessIcon}
                        description={
                            radioValue === 'yearly' && (
                                <span className='MonthlyIncome__annualIncomeDescription'>
                                    Your Total Monthly Income: $
                                    {monthlyIncome}
                                </span>
                            )
                        }
                        showExclamationError={
                            // Workaround insert the Monthly Income Amount and retain the error symbol
                            // If Yearly Income Variant wins the A/B Test then refactor InputBase to allow inserting of child elements during errors states
                            !(
                                isGrossIncomeVariant 
                                && radioValue === 'yearly'
                                && monthlyIncome
                                && Number(cleanNumericInput(monthlyIncome)) > 0
                            )
                        }
                        error={inputError}
                        invalidErrorMessage={
                            (
                                <>
                                    {radioValue === 'yearly' &&
                                        monthlyIncome &&
                                        Number(
                                            cleanNumericInput(monthlyIncome),
                                        ) > 0 && (
                                        <span className='MonthlyIncome__annualIncomeDescription'>
                                                Your Total Monthly Income: $
                                                {monthlyIncome}
                                        </span>
                                    )}
                                    <span
                                        className={
                                            // Workaround insert the Monthly Income Amount and retain the error symbol
                                            // If Yearly Income Variant wins the A/B Test then refactor InputBase to allow inserting of child elements during errors states
                                            isGrossIncomeVariant
                                            && radioValue === 'yearly'
                                            && monthlyIncome
                                            && Number( cleanNumericInput(monthlyIncome)) > 0
                                                ? 'Legacy-InputBase__description--errorWithExclamation'
                                                : ''
                                        }
                                    >
                                        {inputErrorText}
                                    </span>
                                </>
                            )
                        }
                        errorText={
                            (
                                <>
                                    {radioValue === 'yearly' &&
                                        monthlyIncome &&
                                        Number(
                                            cleanNumericInput(monthlyIncome),
                                        ) > 0 && (
                                        <span className='MonthlyIncome__annualIncomeDescription'>
                                                Your Total Monthly Income: $
                                            {monthlyIncome}
                                        </span>
                                    )}
                                    <span
                                        className={
                                            // Workaround insert the Monthly Income Amount and retain the error symbol
                                            // If Yearly Income Variant wins the A/B Test then refactor InputBase to allow inserting of child elements during errors states
                                            isGrossIncomeVariant
                                            && radioValue === 'yearly'
                                            && monthlyIncome
                                            && Number(cleanNumericInput(monthlyIncome)) > 0
                                                ? 'Legacy-InputBase__description--errorWithExclamation'
                                                : ''
                                        }
                                    >
                                        {inputErrorText}
                                    </span>
                                </>
                            )
                        }
                        {...props}
                    />
                    {isIncomeCalculator && (
                        <IncomeCalculatorButton
                            onClickCallback={handleOpenCalculator}
                        />
                    )}
                </div>
                {incomeMessage && (
                    <SlideDown
                        open={showDesc}
                        className='MonthlyIncome__note MonthlyIncome__slideDown'
                        tabIndex={-1}
                    >
                        {transformHTMLParserLinks(
                            wrapTrademarkSuperscript(incomeMessage),
                        )}
                        {incomeDescription && (
                            <>
                                <br />
                                <a
                                    className='MonthlyIncome__descriptionLink'
                                    tabIndex={0}
                                    onClick={() => setShowLongDesc(true)}
                                    onBlur={() => setShowLongDesc(false)}
                                >
                                    Read More:
                                </a>
                                <SlideDown
                                    open={showLongDesc}
                                    className='MonthlyIncome__note'>
                                    {transformHTMLParserLinks(
                                        wrapTrademarkSuperscript(
                                            incomeDescription,
                                        ),
                                    )}
                                </SlideDown>
                            </>
                        )}
                    </SlideDown>
                )}
                {!isGrossIncomeVariant && isIncomeCalculator && showModal && (
                    <IncomeCalculator
                        monthlyIncome={monthlyIncome}
                        setMonthlyIncome={setMonthlyIncome}
                        calculatorValue={calculatorValue}
                        setCalculatorValue={setCalculatorValue}
                        closeCallback={closeCalculatorCallback}
                    />
                )}
            </div>
        );
    },
);

export default MonthlyIncomeField;
